/**
 * ===================================================
 * Module: frontend.src.services.cognito.ts
 * Description: AWS Cognito authentication service
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  ISignUpResult,
} from "amazon-cognito-identity-js";
import i18n from "../i18n/config";

// Cognito configuration from environment variables
const COGNITO_USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_ENDPOINT = process.env.REACT_APP_COGNITO_ENDPOINT || ""; // For local development

// Validate configuration
if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
  console.warn(
    "Cognito configuration missing. Set REACT_APP_COGNITO_USER_POOL_ID and REACT_APP_COGNITO_CLIENT_ID"
  );
}

// Create pool configuration
const poolData = {
  UserPoolId: COGNITO_USER_POOL_ID,
  ClientId: COGNITO_CLIENT_ID,
  ...(COGNITO_ENDPOINT && { endpoint: COGNITO_ENDPOINT }),
};

// Initialize User Pool
const userPool = new CognitoUserPool(poolData);

export interface CognitoAuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface CognitoUserData {
  sub: string;
  email: string;
  emailVerified: boolean;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

/** Thrown by signIn when Cognito requires a permanent password before it
 *  will issue tokens. `complete()` finishes the same sign-in attempt. */
export class NewPasswordRequiredError extends Error {
  readonly email: string;
  readonly complete: (newPassword: string) => Promise<CognitoAuthResult>;

  constructor(
    email: string,
    complete: (newPassword: string) => Promise<CognitoAuthResult>,
  ) {
    super("NEW_PASSWORD_REQUIRED");
    this.name = "NewPasswordRequiredError";
    this.email = email;
    this.complete = complete;
  }
}

class CognitoService {
  /**
   * Sign in user with email and password
   * Uses USER_PASSWORD_AUTH flow for compatibility with cognito-local
   */
  async signIn(email: string, password: string): Promise<CognitoAuthResult> {
    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: email.toLowerCase(),
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

      // Use USER_PASSWORD_AUTH flow (required for cognito-local compatibility)
      // SRP auth is not supported by cognito-local emulator
      cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

      const storeSession = (session: CognitoUserSession): CognitoAuthResult => {
        const result: CognitoAuthResult = {
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          expiresIn: session.getAccessToken().getExpiration(),
        };
        localStorage.setItem("access_token", result.accessToken);
        localStorage.setItem("id_token", result.idToken);
        localStorage.setItem("refresh_token", result.refreshToken);
        return result;
      };

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session: CognitoUserSession) => {
          resolve(storeSession(session));
        },
        onFailure: (err) => {
          reject(this.handleCognitoError(err));
        },
        // An invited account signs in with a temporary password and Cognito
        // withholds every token until a permanent one is set. Hand the caller
        // a way to finish the exchange on this same authentication attempt.
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          const complete = (newPassword: string) =>
            new Promise<CognitoAuthResult>((done, fail) => {
              // Cognito refuses attributes it manages itself (email_verified
              // and the like), so send back only the ones it actually asked for.
              const answers: Record<string, string> = {};
              (requiredAttributes || []).forEach((name: string) => {
                if (userAttributes && userAttributes[name] != null) {
                  answers[name] = userAttributes[name];
                }
              });
              cognitoUser.completeNewPasswordChallenge(newPassword, answers, {
                onSuccess: (session: CognitoUserSession) => {
                  done(storeSession(session));
                },
                onFailure: (err) => {
                  fail(this.handleCognitoError(err));
                },
              });
            });
          reject(new NewPasswordRequiredError(email.toLowerCase(), complete));
        },
      });
    });
  }

  /**
   * Sign up new user
   */
  async signUp(
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phoneNumber?: string,
    companyName?: string,
    address?: string
  ): Promise<ISignUpResult> {
    return new Promise((resolve, reject) => {
      const attributeList: CognitoUserAttribute[] = [
        new CognitoUserAttribute({ Name: "email", Value: email.toLowerCase() }),
      ];

      if (firstName) {
        attributeList.push(
          new CognitoUserAttribute({ Name: "given_name", Value: firstName })
        );
      }
      if (lastName) {
        attributeList.push(
          new CognitoUserAttribute({ Name: "family_name", Value: lastName })
        );
      }
      if (companyName) {
        attributeList.push(
          new CognitoUserAttribute({ Name: "custom:company_name", Value: companyName })
        );
      }
      if (address) {
        attributeList.push(
          new CognitoUserAttribute({ Name: "custom:address", Value: address })
        );
      }
      if (phoneNumber) {
        // Ensure phone number is in E.164 format
        const formattedPhone = phoneNumber.startsWith("+")
          ? phoneNumber
          : `+${phoneNumber}`;
        attributeList.push(
          new CognitoUserAttribute({ Name: "phone_number", Value: formattedPhone })
        );
      }

      userPool.signUp(
        email.toLowerCase(),
        password,
        attributeList,
        [],
        (err, result) => {
          if (err) {
            reject(this.handleCognitoError(err));
            return;
          }
          resolve(result!);
        }
      );
    });
  }

  /**
   * Confirm sign up with verification code
   */
  async confirmSignUp(email: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(this.handleCognitoError(err));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Resend confirmation code
   */
  async resendConfirmationCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(this.handleCognitoError(err));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Initiate forgot password flow
   */
  async forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(this.handleCognitoError(err));
        },
      });
    });
  }

  /**
   * Confirm forgot password with new password
   */
  async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(this.handleCognitoError(err));
        },
      });
    });
  }

  /**
   * Sign out user
   */
  signOut(): void {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("refresh_token");
  }

  /**
   * Get current session (refresh if needed)
   */
  async getSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          this.signOut();
          resolve(null);
          return;
        }

        if (session && session.isValid()) {
          // Update stored token
          localStorage.setItem("access_token", session.getAccessToken().getJwtToken());
          resolve(session);
        } else {
          this.signOut();
          resolve(null);
        }
      });
    });
  }

  /**
   * Get current access token
   */
  async getAccessToken(): Promise<string | null> {
    const session = await this.getSession();
    return session?.getAccessToken().getJwtToken() || null;
  }

  /**
   * Get current user attributes
   */
  async getUserAttributes(): Promise<CognitoUserData | null> {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        cognitoUser.getUserAttributes((err, attributes) => {
          if (err || !attributes) {
            resolve(null);
            return;
          }

          const userData: CognitoUserData = {
            sub: "",
            email: "",
            emailVerified: false,
          };

          attributes.forEach((attr) => {
            switch (attr.Name) {
              case "sub":
                userData.sub = attr.Value;
                break;
              case "email":
                userData.email = attr.Value;
                break;
              case "email_verified":
                userData.emailVerified = attr.Value === "true";
                break;
              case "given_name":
                userData.firstName = attr.Value;
                break;
              case "family_name":
                userData.lastName = attr.Value;
                break;
              case "phone_number":
                userData.phoneNumber = attr.Value;
                break;
            }
          });

          resolve(userData);
        });
      });
    });
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem("access_token");
  }

  /**
   * Handle Cognito errors and return user-friendly messages
   */
  private handleCognitoError(err: any): Error {
    const code = err.code || err.name;
    
    switch (code) {
      case "NotAuthorizedException":
        return new Error(i18n.t("errors.incorrectCredentials", { ns: "auth" }));
      case "UserNotFoundException":
        return new Error(i18n.t("errors.userNotFound", { ns: "auth" }));
      case "UserNotConfirmedException":
        return new Error(i18n.t("errors.pleaseVerifyEmail", { ns: "auth" }));
      case "UsernameExistsException":
        return new Error(i18n.t("errors.userAlreadyExists", { ns: "auth" }));
      case "InvalidPasswordException":
        return new Error(i18n.t("errors.invalidPassword", { ns: "auth" }));
      case "CodeMismatchException":
        return new Error(i18n.t("errors.invalidCode", { ns: "auth" }));
      case "ExpiredCodeException":
        return new Error(i18n.t("errors.expiredCode", { ns: "auth" }));
      case "LimitExceededException":
        return new Error(i18n.t("errors.tooManyAttempts", { ns: "auth" }));
      case "InvalidParameterException":
        return new Error(err.message || i18n.t("errors.invalidParameters", { ns: "auth" }));
      default:
        return new Error(err.message || i18n.t("errors.genericError", { ns: "auth" }));
    }
  }
}

export const cognitoService = new CognitoService();

